import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { Layout } from "@/components/Layout";
import { canonicalGameLinkParts, gamePaths, resultPathForStoredSlug } from "@/lib/game-routing";
import { Card, CardContent } from "@/components/ui/card";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { NumberRow } from "@/components/NumberPill";
import { GameLogo } from "@/components/GameLogo";
import { useResultsByDate } from "@/hooks/use-results-hooks";
import { CalendarDays, ChevronRight, ChevronLeft } from "lucide-react";
import { DrawJsonLd } from "@/components/DrawJsonLd";
import { isAvailableResultDate } from "@/data/results";
import { formatDate } from "@/lib/format";
import { isArchivedResultPath } from "@/lib/seo-policy";

export const Route = createFileRoute("/results/$date")({
  loader: ({ params }) => {
    if (!isAvailableResultDate(params.date)) throw notFound();
    return { date: params.date };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Lottery results unavailable — USA Lottery Club" },
          { name: "robots", content: "noindex, nofollow" },
        ],
      };
    }
    const path = `/results/${params.date}`;
    const seo = buildSeo({
    title: `Lottery Results for ${formatDate(params.date)} — USA Lottery Club`,
    description: `All US lottery winning numbers drawn on ${formatDate(params.date)} — Powerball, Mega Millions, and every state lottery game.`,
    path,
    type: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: `US Lottery Winning Numbers — ${params.date}`,
      description: `All US lottery winning numbers drawn on ${params.date}, including Powerball, Mega Millions, and state lottery games.`,
      url: `https://usalotteryclub.com/results/${params.date}`,
      temporalCoverage: params.date,
      isAccessibleForFree: true,
      creator: {
        "@type": "Organization",
        name: "USA Lottery Club",
        url: "https://usalotteryclub.com",
      },
    },
    });
    if (!isArchivedResultPath(path)) return seo;
    // Old daily roll-ups stay crawlable but out of the index.
    return { ...seo, meta: [...seo.meta, { name: "robots", content: "noindex, follow" }] };
  },
  component: ResultsByDatePage,
  errorComponent: ({ error }) => (
    <Layout>
      <div className="container py-10">
        <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
        <p className="text-muted-foreground">{error?.message || "Failed to load results."}</p>
      </div>
    </Layout>
  ),
  notFoundComponent: () => (
    <Layout>
      <div className="container py-10">
        <h1 className="text-2xl font-bold">Page not found</h1>
      </div>
    </Layout>
  ),
});

function ResultsByDatePage() {
  const { date } = Route.useParams();
  const { data: results, isLoading, isError, error } = useResultsByDate(date);

  // Parse YYYY-MM-DD as UTC to avoid timezone shifts when computing adjacent days.
  const [y, m, d] = date.split("-").map(Number);
  const parsed = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  const dateLabel = parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  const todayIso = new Date().toISOString().slice(0, 10);
  const shift = (days: number) => {
    const t = new Date(parsed);
    t.setUTCDate(t.getUTCDate() + days);
    return t.toISOString().slice(0, 10);
  };
  const prevIso = shift(-1);
  const nextIso = shift(1);
  const isFutureDate = date > todayIso;

  // For dates in the future, never show generated/static data — show empty state.
  const list = isFutureDate ? [] : (results || []);
  const multistate = list.filter((r: any) => r.games?.is_multistate);
  const stateGames = list.filter((r: any) => !r.games?.is_multistate);

  // Group state games by state name
  const byState = new Map<string, any[]>();
  for (const r of stateGames) {
    const name = r.games?.states?.name || "Other";
    if (!byState.has(name)) byState.set(name, []);
    byState.get(name)!.push(r);
  }
  const stateGroups = Array.from(byState.entries()).sort((a, b) => a[0].localeCompare(b[0]));

  const renderRow = (r: any) => {
    const game = r.games;
    const slug = game?.slug;
    const stateAbbr = game?.states?.abbreviation;
    const row = (
      <Card className="transition-colors hover:bg-accent/40">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-4">
            {slug && (
              <GameLogo
                gameSlug={slug}
                gameName={game?.name || ""}
                logoUrl={game?.logo_url}
                size="sm"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-display font-semibold text-sm">{game?.name}</span>
                {stateAbbr && (
                  <span className="text-xs text-muted-foreground">{stateAbbr}</span>
                )}
              </div>
              <NumberRow
                numbers={r.winning_numbers || []}
                bonusNumbers={r.bonus_numbers || []}
                multiplier={r.multiplier}
                size="sm"
                align="left"
              />
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
          </div>
        </CardContent>
      </Card>
    );
    if (!slug) return <div key={r.id}>{row}</div>;
    return (
      <ResultLink key={r.id} storedSlug={slug} date={r.draw_date}>
        {row}
      </ResultLink>
    );
  };

  return (
    <Layout>
      <div className="container py-6 md:py-10">
        <DrawJsonLd
          draws={list.map((r: any) => ({
            gameName: r.games?.name ?? "",
            gameSlug: r.games?.slug ?? "",
            drawDate: r.draw_date,
            winningNumbers: r.winning_numbers ?? [],
            bonusNumbers: r.bonus_numbers ?? [],
            multiplier: r.multiplier ?? null,
            stateName: r.games?.states?.name ?? null,
            url: `https://usalotteryclub.com${resultPathForStoredSlug(r.games?.slug ?? "", r.draw_date)}`,
          }))}
        />
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/results">Results</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{dateLabel}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="h-7 w-7 text-primary" />
          <h1 className="font-sans text-2xl md:text-3xl font-bold">
            Lottery Results — {dateLabel}
          </h1>
        </div>
        <p className="text-muted-foreground max-w-3xl mb-6">
          All US lottery results for {dateLabel}. Check winning numbers for Powerball, Mega Millions,
          and state lottery games drawn on this date.
        </p>

        <div className="flex flex-wrap gap-2 mb-8">
          <Link
            to="/results/$date"
            params={{ date: prevIso }}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border bg-card hover:bg-accent transition-colors text-sm"
          >
            <ChevronLeft className="h-4 w-4" /> Previous Day
          </Link>
          <Link
            to="/results/$date"
            params={{ date: todayIso }}
            className="inline-flex items-center px-3 py-1.5 rounded-md border bg-card hover:bg-accent transition-colors text-sm"
          >
            Today
          </Link>
          {nextIso <= todayIso && (
            <Link
              to="/results/$date"
              params={{ date: nextIso }}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border bg-card hover:bg-accent transition-colors text-sm"
            >
              Next Day <ChevronRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        {!isFutureDate && isLoading && (
          <Card>
            <CardContent className="py-16 text-center">
              <p className="text-muted-foreground">Loading results…</p>
            </CardContent>
          </Card>
        )}
        {!isFutureDate && !isLoading && isError && (
          <Card>
            <CardContent className="py-16 text-center">
              <p className="text-lg font-medium mb-1">Couldn't load results</p>
              <p className="text-sm text-muted-foreground">
                {(error as any)?.message || "Please try again in a moment."}
              </p>
            </CardContent>
          </Card>
        )}
        {((isFutureDate) || (!isLoading && !isError && list.length === 0)) && (
          <Card>
            <CardContent className="py-16 text-center flex flex-col items-center">
              <CalendarDays className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-lg font-semibold mb-1">No results available</p>
              <p className="text-sm text-muted-foreground max-w-md">
                {isFutureDate
                  ? `Results for ${dateLabel} haven't been drawn yet. Check back after the scheduled drawings.`
                  : `No lottery drawings were posted for ${dateLabel}. Try another date.`}
              </p>
              <div className="flex gap-2 mt-4">
                <Link
                  to="/results/$date"
                  params={{ date: prevIso }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border bg-card hover:bg-accent transition-colors text-sm"
                >
                  <ChevronLeft className="h-4 w-4" /> Previous Day
                </Link>
                <Link
                  to="/results/$date"
                  params={{ date: todayIso }}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border bg-card hover:bg-accent transition-colors text-sm"
                >
                  Today
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {multistate.length > 0 && (
          <>
            <h2 className="font-sans font-bold mb-4" style={{ fontSize: "20px" }}>Multi-State Games</h2>
            <div className="space-y-3 mb-8">{multistate.map(renderRow)}</div>
          </>
        )}

        {stateGroups.map(([stateName, rows]) => (
          <div key={stateName} className="mb-8">
            <h2 className="font-sans font-bold mb-4" style={{ fontSize: "20px" }}>{stateName}</h2>
            <div className="space-y-3">{rows.map(renderRow)}</div>
          </div>
        ))}
      </div>
    </Layout>
  );
}

/** Links a draw row to the canonical result URL for that game. */
function ResultLink({ storedSlug, date, children }: { storedSlug: string; date: string; children: React.ReactNode }) {
  const { gameSlug, stateSlug } = canonicalGameLinkParts(storedSlug);
  const AnyLink = Link as unknown as React.ComponentType<Record<string, unknown>>;
  return (
    <AnyLink to={gamePaths(gameSlug, stateSlug).result(date)} className="block">
      {children}
    </AnyLink>
  );
}
